import { Container, Graphics } from 'pixi.js';
import type { ColorSource, PointData } from 'pixi.js';
import { registerResizableObject } from '../../../utils/pixi/registerResizableObject';

export interface ColorOverlayOptions {
  fillColor: ColorSource;
  fillOpacity: number;
  blockInteractivity: boolean;
  anchor: PointData;
}

const defaultOptions: ColorOverlayOptions = {
  fillColor: 'black',
  fillOpacity: 0.5,
  blockInteractivity: true,
  anchor: { x: 0.5, y: 0.5 },
};

export class ColorOverlay extends Container {
  private readonly graphics: Graphics;

  private readonly anchor: PointData;

  private fillOpacity: number;

  constructor(private readonly options: Partial<ColorOverlayOptions> = {}) {
    super();

    const { fillColor, fillOpacity, blockInteractivity, anchor }: ColorOverlayOptions = {
      ...defaultOptions,
      ...options,
    };

    this.fillOpacity = fillOpacity;

    this.anchor = anchor;

    this.graphics = new Graphics().rect(0, 0, 800, 800).fill({ color: fillColor, alpha: fillOpacity }).props({
      parent: this,
    });

    this.interactive = !blockInteractivity;
    this.interactiveChildren = false;

    this.alpha = 0;
    this.visible = false;

    registerResizableObject(this);
  }

  public resize(width: number, height: number): void {
    const { graphics, anchor } = this;
    graphics.width = width;
    graphics.height = height;
    graphics.x = anchor.x * -graphics.width;
    graphics.y = anchor.y * -graphics.height;
  }

  public setFillOpacity(fillOpacity: number): void {
    if (this.fillOpacity === fillOpacity) return;
    this.fillOpacity = fillOpacity;
    this.graphics.clear().rect(0, 0, 800, 800).fill({ color: this.options.fillColor, alpha: fillOpacity });
  }

  public fadeIn(duration = 500): void {
    this.visible = true;
    this.removeTweens(true);
    this.tweenTo({ alpha: 1 }, duration, 'quadOut', true);
  }

  public fadeOut(duration = 500): void {
    if (this.alpha === 0) return;
    this.removeTweens(true);
    const tween = this.tweenTo({ alpha: 0 }, duration, 'quadOut', true);
    tween.onComplete.addOnce(() => {
      this.visible = false;
    });
  }

  public flash(duration = 120, delay = 0): void {
    this.visible = true;
    this.removeTweens(true);
    this.alpha = 0;
    this.tweenTo({ alpha: 1 }, duration, 'cubicIn', true, delay, undefined, true).onComplete.addOnce(() => {
      this.visible = false;
    });
  }
}
